import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addBannerTypes,
  addLogoUrlTypes,
  addSupplierLogoTypes,
  editFooterTypes,
  editSupplierTypes,
  footerTypes,
  initialFooterTypes,
  PublicationBannersTypes,
  PublicationContactTypes,
  PublicationPoliciesTypes,
  PublicationTheme,
  publicationTypes,
  requestPublicationTypes,
  SuccessType,
  supplierTypes,
  userRoleTypes,
  UsersCollection,
} from "./adminTypes";
import { editMetadataTypes } from "../articles/articlesTypes";
import adminService from "./adminService";

interface adminState {
  publicationTheme: PublicationTheme | null;
  publications: publicationTypes[];
  editedPublication: publicationTypes | null;
  supplier: supplierTypes | null;
  suppliers: supplierTypes[];
  loading: boolean;
  loadingUsers: boolean;
  magazineLoading: boolean;
  magazineThemeLoading: boolean;
  error: any | null;
  users: userRoleTypes[] | UsersCollection | null;
  article_slug: string | null;
  initialFooter: initialFooterTypes | null;
  publicationFooter: footerTypes | null;
  publicationContact: PublicationContactTypes | null;
  publicationBanners: PublicationBannersTypes | null;
  footerLoading: boolean;
  contactLoading: boolean;
  bannersLoading: boolean;
}

const initialState: adminState = {
  publicationTheme: null,
  publications: [],
  editedPublication: null,
  supplier: null,
  suppliers: [],
  magazineLoading: false,
  magazineThemeLoading: false,
  loading: false,
  loadingUsers: false,
  error: null,
  users: null,
  article_slug: null,
  initialFooter: null,
  publicationFooter: null,
  publicationContact: null,
  publicationBanners: null,
  footerLoading: false,
  contactLoading: false,
  bannersLoading: false,
};

export const getPublications = createAsyncThunk<
  publicationTypes[],
  void,
  { rejectValue: string }
>("admin/getPublications", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublications();
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getPublicationBySlug = createAsyncThunk<
  publicationTypes,
  string,
  { rejectValue: string }
>("admin/getPublicationBySlug", async (slug, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublicationBySlug(slug);
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getPublicationStyle = createAsyncThunk<
  PublicationTheme,
  void,
  { rejectValue: string }
>("admin/getPublicationStyle", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublicationStyle();
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const createPublication = createAsyncThunk<
  SuccessType,
  requestPublicationTypes,
  { rejectValue: string }
>(
  "admin/createPublication",
  async (data: requestPublicationTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.createPublication(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const createPublicationAdmin = createAsyncThunk<
  SuccessType,
  requestPublicationTypes,
  { rejectValue: string }
>(
  "admin/createPublicationAdmin",
  async (data: requestPublicationTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.createPublicationAdmin(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const editPublication = createAsyncThunk<
  SuccessType,
  any,
  { rejectValue: string }
>("admin/editPublication", async (data: any, { rejectWithValue }) => {
  try {
    const response = await adminService.editPublication(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deletePublication = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("admin/deletePublication", async (slug, { rejectWithValue }) => {
  try {
    const res = await adminService.deletePublication(slug);
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const createArticle = createAsyncThunk<
  string,
  FormData,
  { rejectValue: string }
>("admin/createArticle", async (data: FormData, { rejectWithValue }) => {
  try {
    const response = await adminService.createArticle(data);

    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getSuppliers = createAsyncThunk<
  supplierTypes[],
  void,
  { rejectValue: string }
>("admin/getSuppliers", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getSuppliers();
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getSupplier = createAsyncThunk<
  supplierTypes,
  string,
  { rejectValue: string }
>("admin/getSupplier", async (supplier_name, { rejectWithValue }) => {
  try {
    const res = await adminService.getSupplier(supplier_name);
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const createSupplier = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("admin/createSupplier", async (name: string, { rejectWithValue }) => {
  try {
    const response = await adminService.createSupplier(name);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const editSupplier = createAsyncThunk<
  SuccessType,
  editSupplierTypes,
  { rejectValue: string }
>(
  "admin/editSupplier",
  async (data: editSupplierTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.editSupplier(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const deleteSupplier = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("admin/deleteSupplier", async (name, { rejectWithValue }) => {
  try {
    const res = await adminService.deleteSupplier(name);
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const editMetadata = createAsyncThunk<
  string,
  editMetadataTypes,
  { rejectValue: string }
>("admin/editMetadata", async (editMetadataRequest, { rejectWithValue }) => {
  try {
    const response = await adminService.editMetadata(editMetadataRequest);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addLogoUrl = createAsyncThunk<
  SuccessType,
  addLogoUrlTypes,
  { rejectValue: string }
>("admin/addLogoUrl", async (data: addLogoUrlTypes, { rejectWithValue }) => {
  try {
    const response = await adminService.addLogoUrl(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addFavicon = createAsyncThunk<SuccessType, addLogoUrlTypes>(
  "admin/addFavicon",
  async (data: addLogoUrlTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.addFavicon(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addSupplierLogo = createAsyncThunk<
  SuccessType,
  addSupplierLogoTypes,
  { rejectValue: string }
>(
  "admin/addSupplierLogo",
  async (addSupplierLogoRequest: addSupplierLogoTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.addSupplierLogo(
        addSupplierLogoRequest,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addBanner = createAsyncThunk<
  SuccessType,
  any,
  { rejectValue: string }
>("admin/addBanner", async (addBannerRequest: any, { rejectWithValue }) => {
  try {
    const response = await adminService.addBanner(addBannerRequest);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addBannerImg = createAsyncThunk<
  string,
  addBannerTypes,
  { rejectValue: string }
>(
  "admin/addBannerImg",
  async (addBannerImgRequest: addBannerTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.addBannerImg(addBannerImgRequest);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getRoles = createAsyncThunk<
  userRoleTypes[],
  void,
  { rejectValue: string }
>("admin/getRoles", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getRoles();
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getRolesByUser = createAsyncThunk<
  UsersCollection,
  void,
  { rejectValue: string }
>("admin/getRolesByUser", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getRolesByUser();
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getOrCreateSupplier = createAsyncThunk<
  supplierTypes,
  string,
  { rejectValue: string }
>("admin/getOrCreateSupplier", async (supplier_name, thunkAPI) => {
  try {
    const res = await adminService.getOrCreateSupplier(supplier_name);
    return res;
  } catch (error: any) {
    console.error("Error en getOrCreateSupplier:", error);
    return thunkAPI.rejectWithValue(error);
  }
});

export const getPublicationFooter = createAsyncThunk<
  PublicationPoliciesTypes,
  void,
  { rejectValue: string }
>("admin/getPublicationFooter", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationFooter();
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getInitialFooter = createAsyncThunk<
  initialFooterTypes,
  void,
  { rejectValue: string }
>("admin/getInitialFooter", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getInitialFooter();
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getPublicationContact = createAsyncThunk<
  PublicationContactTypes,
  void,
  { rejectValue: string }
>("admin/getPublicationContact", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationContact();
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getPublicationBanners = createAsyncThunk<
  PublicationBannersTypes,
  void,
  { rejectValue: string }
>("admin/getPublicationBanners", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationBanners();
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const editPublicationFooter = createAsyncThunk<
  SuccessType,
  editFooterTypes,
  { rejectValue: string }
>(
  "admin/editPublicationFooter",
  async (editFooterRequest: editFooterTypes, { rejectWithValue }) => {
    try {
      return await adminService.editPublicationFooter(editFooterRequest);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const reparseWord = createAsyncThunk<
  SuccessType,
  { article_id: number; url: string },
  { rejectValue: string }
>("admin/reparseWord", async ({ article_id, url }, { rejectWithValue }) => {
  try {
    return await adminService.reparseWord(article_id, url);
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const uploadHowToReviewFile = createAsyncThunk<
  SuccessType,
  FormData,
  { rejectValue: string }
>("admin/uploadHowToReviewFile", async (formData, { rejectWithValue }) => {
  try {
    return await adminService.uploadHowToReviewFile(formData);
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const deleteHowToReviewFile = createAsyncThunk<
  SuccessType,
  void,
  { rejectValue: string }
>("admin/deleteHowToReviewFile", async (_, { rejectWithValue }) => {
  try {
    return await adminService.deleteHowToReviewFile();
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    resetPublication: (state) => {
      state.publicationTheme = null;
      state.publications = [];
      state.editedPublication = null;
      state.supplier = null;
      state.suppliers = [];
      state.users = null;
      state.publicationContact = null;
      state.publicationBanners = null;
    },
  },
  extraReducers: (builder) => {
    // --- Publications ---
    builder.addCase(getPublications.fulfilled, (state, action) => {
      state.publications = action.payload;
    });

    // --- Publication Detail / Theme ---
    builder.addCase(getPublicationBySlug.fulfilled, (state, action) => {
      state.editedPublication = action.payload;
    });
    builder.addCase(getPublicationStyle.fulfilled, (state, action) => {
      state.publicationTheme = action.payload;
    });

    builder.addCase(createArticle.fulfilled, (state, action) => {
      state.article_slug = action.payload;
    });

    // --- Suppliers ---
    builder.addCase(getSuppliers.fulfilled, (state, action) => {
      state.suppliers = action.payload;
    });
    builder.addCase(getSupplier.pending, (state) => {
      state.supplier = null;
    });
    builder.addCase(getSupplier.fulfilled, (state, action) => {
      state.supplier = action.payload;
    });
    builder.addCase(getOrCreateSupplier.fulfilled, (state, action) => {
      state.supplier = action.payload;
    });

    // --- Roles / Users ---
    builder.addCase(getRoles.pending, (state) => {
      state.users = null;
    });
    builder.addCase(getRoles.fulfilled, (state, action) => {
      state.users = action.payload;
    });
    builder
      .addCase(getRolesByUser.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loadingUsers = false;
        state.error = null;
      })
      .addCase(getRolesByUser.pending, (state) => {
        state.users = null;
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(getRolesByUser.rejected, (state, action) => {
        state.users = null;
        state.loadingUsers = false;
        state.error = action.payload;
      });

    builder.addCase(getPublicationFooter.fulfilled, (state, action) => {
      state.publicationFooter = action.payload;
    });
    builder.addCase(getInitialFooter.fulfilled, (state, action) => {
      state.initialFooter = action.payload;
    });
    builder.addCase(getPublicationContact.fulfilled, (state, action) => {
      state.publicationContact = action.payload;
    });
    builder.addCase(getPublicationBanners.fulfilled, (state, action) => {
      state.publicationBanners = action.payload;
    });
    builder.addCase(reparseWord.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(reparseWord.rejected, (state, action) => {
      state.error = action.payload || "Error desconocido";
    });
    builder.addCase(reparseWord.pending, (state) => {
      state.error = null;
    });
    builder.addCase(uploadHowToReviewFile.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(uploadHowToReviewFile.rejected, (state, action) => {
      state.error = action.payload || "Error desconocido";
    });
    builder.addCase(uploadHowToReviewFile.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deleteHowToReviewFile.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(deleteHowToReviewFile.rejected, (state, action) => {
      state.error = action.payload || "Error desconocido";
    });
    builder.addCase(deleteHowToReviewFile.pending, (state) => {
      state.error = null;
    });

    const generalLoadingActions = [
      getPublications,
      createPublication,
      createPublicationAdmin,
      editPublication,
      deletePublication,
      createArticle,
      getSuppliers,
      createSupplier,
      editSupplier,
      deleteSupplier,
      addLogoUrl,
      addFavicon,
      addSupplierLogo,
      getRoles,
      getRolesByUser,
      getOrCreateSupplier,
      editMetadata,
      addBanner,
      addBannerImg,
      editPublicationFooter,
    ];

    builder.addMatcher(
      (action) =>
        generalLoadingActions.some((act) => action.type === act.pending.type),
      (state) => {
        state.loading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        generalLoadingActions.some((act) =>
          [act.fulfilled.type, act.rejected.type].includes(action.type),
        ),
      (state) => {
        state.loading = false;
      },
    );
    const magazineLoadingActions = [getPublicationBySlug, getPublicationStyle];
    builder.addMatcher(
      (action) =>
        magazineLoadingActions.some((act) => action.type === act.pending.type),
      (state) => {
        state.magazineLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        magazineLoadingActions.some((act) =>
          [act.fulfilled.type, act.rejected.type].includes(action.type),
        ),
      (state) => {
        state.magazineLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationFooter.pending.type,
      (state) => {
        state.footerLoading = true;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationFooter.fulfilled.type,
          getPublicationFooter.rejected.type,
        ].includes(action.type),
      (state) => {
        state.footerLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationContact.pending.type,
      (state) => {
        state.contactLoading = true;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationContact.fulfilled.type,
          getPublicationContact.rejected.type,
        ].includes(action.type),
      (state) => {
        state.contactLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationBanners.pending.type,
      (state) => {
        state.bannersLoading = true;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationBanners.fulfilled.type,
          getPublicationBanners.rejected.type,
        ].includes(action.type),
      (state) => {
        state.bannersLoading = false;
      },
    );

    // ✅ CORREGIDO - Eliminé el tipo problemático
    builder.addMatcher(
      (action) =>
        action.type.startsWith("admin/") && action.type.endsWith("/rejected"),
      (state, action: PayloadAction<string>) => {
        state.error = action.payload || "Error desconocido";
        state.loading = false;
      },
    );
  },
});
export const { resetPublication } = adminSlice.actions;
export default adminSlice.reducer;
